import React, { Component } from 'react';
import {
  Grid,
  MenuItem,
  Select,
  Autocomplete,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Stack,
} from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import checkKey from '../../utils/CheckKey';
import CloseIcon from '@mui/icons-material/Close';
import { withStyles } from '@mui/styles';
import ChipInput from '../ChipInput/ChipInput';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ListIcon from '@mui/icons-material/List';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import styles from '../../styles/DataTableStyle';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { FileCopyOutlined } from '@mui/icons-material';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import { isPlaceholder } from '../../utils/PlaceholderUtil';
import { QUERY_OUTPUT_TYPES } from '../QueryDatabase/QueryDatabaseUtil';
import OutputColumnDropdown from './RenderQuery/OutputColumnDropdown';
import { QueryConditionals } from './RenderQuery/QueryConditionals';
import { RecordIdInput } from './RenderQuery/RecordIdInput';
import { getColumnValue } from './RenderQuery/DbQueryColumnUtil';
import './stylesheet/RenderQueryItem.css';

const RETURN_TYPE = {
  LIST: 'list',
  NESTED_LIST: 'nestedList',
  STRING: 'string',
  OBJECT_LIST: 'objectList',
};

const RETURN_TYPE_ARRAY = Object.values(RETURN_TYPE);

class RenderQueryItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      checkKey: false,
      errorMessage: '',
      showWarning: false,
    };

    //Adding type to all condition objects
    let queryDataLocal = props.queryData;
    let allColumns = props.availableCols;
    //Loop through every single data component in query data
    for (let queryIndex in queryDataLocal) {
      //Loop through all the conditions in the data component
      for (let index in queryDataLocal[queryIndex].condition) {
        let condition = queryDataLocal[queryIndex].condition[index];
        let column = allColumns.find(col => col.name === condition.columnName);
        let type = 'VARCHAR2';

        if (column) {
          //Session id and database id dont have a type so will automatically give it a type
          type = column.type
            ? allColumns.find(col => col.name === condition.columnName).type
            : 'VARCHAR2';
        }
        queryDataLocal[queryIndex].condition[index].type = type;

        //Adding Input type in queryData e.g. Whether a date time field should show up or a placeholder input box should show up
        if (condition.value && condition.type === 'DATETIME' && isPlaceholder(condition.value)) {
          queryDataLocal[queryIndex].condition[index].datetimeValue = 'placeholder';
        } else {
          queryDataLocal[queryIndex].condition[index].datetimeValue = 'datetime';
        }

        if (!column) {
          condition.columnName = null;
        }
      }
      props.setQueryData(queryDataLocal);
    }
  }

  renderQueryItemDetails = (outputFunc, aData, index) => {
    const { classes, availablePlaceholders, queryData, availableCols, component, validateKey } =
      this.props;

    switch (outputFunc) {
      case QUERY_OUTPUT_TYPES.SELECTED_COLUMN:
        return (
          <>
            <OutputColumnDropdown
              aData={aData}
              component={component}
              onChange={value => this.setOutputValue(index, value, queryData)}
              availableCols={availableCols}
              validateKey={validateKey}
            />
            {component === 'query_database_record'
              ? this.getReturnTypeSelect(aData, index, queryData)
              : ''}
          </>
        );

      case QUERY_OUTPUT_TYPES.FILTERED_COLUMN:
        return (
          <>
            <OutputColumnDropdown
              component={component}
              aData={aData}
              onChange={value => this.setOutputValue(index, value, queryData)}
              availableCols={availableCols}
              validateKey={validateKey}
            />
            {component === 'query_database_record'
              ? this.getReturnTypeSelect(aData, index, queryData)
              : ''}
            <QueryConditionals
              aData={aData}
              availableCols={availableCols}
              availablePlaceholders={availablePlaceholders}
              updateCondition={this.updateCondition}
              deleteQueryItemCondition={this.deleteQueryItemCondition}
              duplicateCondition={this.duplicateCondition}
              index={index}
              queryData={queryData}
              setThisFocus={this.props.setThisFocus}
              onPlaceholderSelected={this.props.onPlaceholderSelected}
              validateKey={validateKey}
              classes={classes}
              onClick={() => {
                this.addCondition(index, queryData);
              }}
            />
          </>
        );

      case QUERY_OUTPUT_TYPES.SELECTED_RECORD:
        return (
          <RecordIdInput
            onPlaceholderSelected={this.props.onPlaceholderSelected}
            placeholders={availablePlaceholders}
            aData={aData}
            index={index}
            classes={classes}
            queryData={queryData}
            setQueryData={this.props.setQueryData}
            setThisFocus={this.props.setThisFocus}
          />
        );

      case QUERY_OUTPUT_TYPES.FILTERED_RECORD:
        return (
          <>
            <QueryConditionals
              aData={aData}
              availableCols={availableCols}
              availablePlaceholders={availablePlaceholders}
              updateCondition={this.updateCondition}
              deleteQueryItemCondition={this.deleteQueryItemCondition}
              duplicateCondition={this.duplicateCondition}
              index={index}
              queryData={queryData}
              setThisFocus={this.props.setThisFocus}
              onPlaceholderSelected={this.props.onPlaceholderSelected}
              validateKey={validateKey}
              classes={classes}
              onClick={() => {
                this.addCondition(index, queryData);
              }}
            />
          </>
        );

      case 'COUNT_COLUMN':
        const countColumnSelected = availableCols.find(column => {
          return getColumnValue(column) === aData.value;
        });

        return (
          <>
            <Grid container spacing={3} alignItems="flex-start" sx={{ mt: 1 }}>
              <Grid item xs="auto">
                <p className="count-column-label-text">Return count of values from</p>
              </Grid>

              <Grid item>
                <Autocomplete
                  className="count-column-autocomplete"
                  value={countColumnSelected || null}
                  onChange={(event, newValue) => {
                    const value = newValue ? getColumnValue(newValue) : null;
                    this.setOutputValue(index, value, queryData);
                  }}
                  options={availableCols}
                  getOptionLabel={option => option.name || ''}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="select column"
                      variant="standard"
                      size="small"
                      className="count-column-textfield"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.placeholderKey || option.realValue}>
                      {option.name}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => {
                    return getColumnValue(option) === getColumnValue(value);
                  }}
                  autoHighlight
                  disableClearable={false}
                  noOptionsText="No columns found"
                />
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={2} sx={{ mt: 1 }}>
              <Grid item xs="auto">
                <p className="count-column-conditions-label-text">Where the data in</p>
              </Grid>
              <Grid item xs={10} direction="column">
                {aData.condition &&
                  aData.condition.map((aDataCondition, aDataConditionIndex) => (
                    <>
                      <Grid item xs>
                        {aDataConditionIndex > 0 && (
                          <Select
                            className={
                              aDataConditionIndex == 0
                                ? 'count-column-logic-select-hidden'
                                : 'count-column-logic-select'
                            }
                            name="selectType"
                            disableUnderline
                            variant="standard"
                            value={aDataCondition.logic}
                            onChange={event => {
                              let logic = event.target.value;

                              this.updateCondition(
                                index,
                                aDataConditionIndex,
                                logic,
                                aDataCondition.key,
                                aDataCondition.function,
                                aDataCondition.value,
                                aDataCondition.datetimeValue,
                                queryData
                              );
                            }}
                          >
                            <MenuItem value="AND">AND</MenuItem>
                            <MenuItem value="OR">OR</MenuItem>
                          </Select>
                        )}
                        <FormControl error={aDataCondition.key == null}>
                          <Autocomplete
                            className="query-count-column-autocomplete"
                            value={(() => {
                              if (!aDataCondition.key) return null;
                              return availableCols.find(
                                column => getColumnValue(column) === aDataCondition.key
                              );
                            })()}
                            onChange={(event, newValue) => {
                              let key = newValue ? getColumnValue(newValue) : null;
                              this.updateCondition(
                                index,
                                aDataConditionIndex,
                                aDataCondition.logic,
                                key,
                                aDataCondition.function,
                                aDataCondition.value,
                                aDataCondition.datetimeValue,
                                queryData
                              );
                            }}
                            options={availableCols}
                            getOptionLabel={option => option.name || ''}
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="select column"
                                variant="standard"
                                size="small"
                                className="count-column-textfield"
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={option.placeholderKey || option.realValue}>
                                {option.name}
                              </li>
                            )}
                            isOptionEqualToValue={(option, value) => {
                              return getColumnValue(option) === getColumnValue(value);
                            }}
                            autoHighlight
                            disableClearable={false}
                            noOptionsText="No columns found"
                          />
                          {aDataCondition.key == null && validateKey ? (
                            <FormHelperText>This field cannot be empty</FormHelperText>
                          ) : (
                            ''
                          )}
                        </FormControl>
                        &nbsp;
                        <FormControl error={aDataCondition.function == null}>
                          <Select
                            className="count-column-function-select"
                            name="selectType"
                            disableUnderline
                            variant="standard"
                            displayEmpty
                            value={aDataCondition.function}
                            onChange={event => {
                              let func = event.target.value;
                              let value = aDataCondition.value;
                              if (func === 'EMPTY' || func === 'NOTEMPTY') {
                                value = '';
                              }
                              this.updateCondition(
                                index,
                                aDataConditionIndex,
                                aDataCondition.logic,
                                aDataCondition.key,
                                func,
                                value,
                                aDataCondition.datetimeValue,
                                queryData
                              );
                            }}
                          >
                            <MenuItem value={null} disabled>
                              {' '}
                              Function{' '}
                            </MenuItem>

                            <MenuItem value="EQUAL">Equals</MenuItem>
                            <MenuItem value="NOT_EQUAL">does not equal</MenuItem>
                            <MenuItem value="MATCHES"> contains </MenuItem>
                            <MenuItem value="NOTMATCH"> does not contain </MenuItem>
                            <MenuItem value="GREATER_THAN">greater than</MenuItem>
                            <MenuItem value="GREATERTHANOREQUAL">greater than or equal to</MenuItem>
                            <MenuItem value="LESS_THAN">less than</MenuItem>
                            <MenuItem value="LESSTHANOREQUAL"> less than or equal to </MenuItem>
                            <MenuItem value="NOTEMPTY"> is not empty </MenuItem>
                            <MenuItem value="EMPTY"> is empty </MenuItem>
                          </Select>
                          {aDataCondition.key == null && validateKey ? (
                            <FormHelperText>This field cannot be empty</FormHelperText>
                          ) : (
                            ''
                          )}
                        </FormControl>
                        &nbsp;
                        {aDataCondition.function === 'EMPTY' ||
                        aDataCondition.function === 'NOTEMPTY' ? (
                          ''
                        ) : (
                          <>
                            <ChipInput
                              className="count-column-chip-input"
                              onPlaceholderSelected={this.props.onPlaceholderSelected}
                              placeholders={availablePlaceholders}
                              onBeforeAdd={chip => {
                                let val = chip;

                                if (val && val.trim().length > 0) {
                                  this.updateCondition(
                                    index,
                                    aDataConditionIndex,
                                    aDataCondition.logic,
                                    aDataCondition.key,
                                    aDataCondition.function,
                                    val,
                                    aDataCondition.datetimeValue,
                                    queryData
                                  );
                                  return true;
                                } else {
                                  return false;
                                }
                              }}
                              placeholder="Type something or insert a placeholder"
                              inputValue={aDataCondition.value ? [aDataCondition.value] : []}
                              onDelete={(chip, indexChip) => {
                                let val = '';
                                this.updateCondition(
                                  index,
                                  aDataConditionIndex,
                                  aDataCondition.logic,
                                  aDataCondition.key,
                                  aDataCondition.function,
                                  val,
                                  aDataCondition.datetimeValue,
                                  queryData
                                );
                              }}
                              onFocus={() => {
                                this.props.setThisFocus(index, aDataConditionIndex);
                              }}
                            />
                            &nbsp;
                          </>
                        )}
                        {aDataConditionIndex > 0 && (
                          <CloseIcon
                            className={`${classes.deleteIcon} count-column-close-icon ${
                              aDataConditionIndex == 0 ? 'count-column-close-icon-hidden' : ''
                            }`}
                            onClick={() =>
                              this.deleteQueryItemCondition(index, aDataConditionIndex, queryData)
                            }
                          />
                        )}
                      </Grid>
                    </>
                  ))}
                <Grid item>
                  <div className="count-column-add-condition">
                    <div
                      className={`${classes.textContentCenter} count-column-add-condition-button`}
                      onClick={() => {
                        this.addCondition(index, queryData);
                      }}
                    >
                      <AddIcon /> Add AND/OR condition
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </>
        );

      default:
        return;
    }
  };

  getReturnTypeSelect(aData, index, queryData) {
    const selectedValue =
      aData?.returnType && RETURN_TYPE_ARRAY.includes(aData.returnType)
        ? aData.returnType
        : RETURN_TYPE.LIST;

    return (
      <Grid container spacing={3} alignItems="center" sx={{ mt: 0.5 }}>
        <Grid item xs="auto">
          <p className="return-type-label-text">as a</p>
        </Grid>
        <Grid item>
          <RadioGroup
            value={selectedValue}
            onChange={event => this.setOutputReturnType(index, event, queryData)}
            row
            className="return-type-radio-group"
          >
            <FormControlLabel
              value={RETURN_TYPE.LIST}
              control={<Radio size="small" />}
              label={this.state.showWarning ? 'List of a list [[A,B,C],[D,E,F]]' : 'List [A,B,C]'}
              className="return-type-radio-label"
            />
            <FormControlLabel
              value={RETURN_TYPE.STRING}
              control={<Radio size="small" />}
              label={this.state.showWarning ? 'String "A,B,C,D,E,F"' : 'String "A,B,C"'}
              className="return-type-radio-label"
            />
            {this.state.showWarning && (
              <FormControlLabel
                value={RETURN_TYPE.NESTED_LIST}
                control={<Radio size="small" />}
                label="List [A,B,C,D,E,F]"
                className="return-type-radio-label"
              />
            )}
          </RadioGroup>
        </Grid>
      </Grid>
    );
  }

  createUpdatedQueryData = (queryData, index, updates) => {
    return queryData.map((item, idx) => {
      if (idx !== index) return item;
      return { ...item, ...updates };
    });
  };

  handleKey = (e, queryData, index) => {
    let keyValue = replacePlaceholderValue(e.target.value);

    const ckValue = checkKey(keyValue, index, queryData);
    this.setState({
      checkKey: ckValue[0],
      errorMessage: ckValue[1],
    });

    let aData = queryData[index];
    aData.key = keyValue;

    this.props.setQueryData(queryData);
  };

  setOutputFunc = (index, event, queryData) => {
    this.props.setValidateKey(false);
    let aData = queryData[index];
    aData.outputFunc = event.target.value;
    aData.returnType = RETURN_TYPE.LIST;

    let outputType = RETURN_TYPE.LIST;

    const injectDefaultCondition = () => {
      //add the 1st condition
      aData.condition = [];
      const condition = {
        key: null,
        function: null,
        value: null,
      };
      aData.condition.push(condition);
    };

    if (aData.outputFunc === QUERY_OUTPUT_TYPES.COUNT_COLUMN) {
      //count so we set the type to number
      outputType = 'number';
    }

    if (
      aData.outputFunc === QUERY_OUTPUT_TYPES.FILTERED_COLUMN ||
      aData.outputFunc === QUERY_OUTPUT_TYPES.COUNT_COLUMN
    ) {
      injectDefaultCondition();
    }

    if (aData.outputFunc === QUERY_OUTPUT_TYPES.SELECTED_COLUMN) {
      delete aData.condition;
    }

    if (aData.outputFunc === QUERY_OUTPUT_TYPES.SELECTED_RECORD) {
      aData.returnType = RETURN_TYPE.LIST;
      delete aData.condition;
    }

    if (aData.outputFunc === QUERY_OUTPUT_TYPES.FILTERED_RECORD) {
      injectDefaultCondition();
      aData.returnType = RETURN_TYPE.LIST;
    }

    delete aData.rowIds;

    aData.outputType = outputType;

    this.props.setQueryData(queryData);
  };

  deleteQueryItem = (index, queryData) => {
    queryData.splice(index, 1);
    this.props.setQueryData(queryData);
  };

  setOutputValue = (index, value, queryData) => {
    if (!queryData || !queryData[index]) {
      return;
    }

    const { availableCols } = this.props;
    const column = availableCols?.find(col => col.placeholderKey === value);
    let aData = queryData[index];
    aData.value = value;
    aData.columnName = column ? column.name : null;
    this.state.showWarning = column ? column.type === 'LIST' : false;
    this.props.setQueryData(queryData);
  };
  setOutputReturnType = (index, event, queryData) => {
    let aData = queryData[index];
    aData.returnType = event.target.value;
    this.props.setQueryData(queryData);
  };

  updateCondition = (index, conditionIndex, logic, key, func, value, datetimeValue, queryData) => {
    const { availableCols } = this.props;
    const column = availableCols.find(col => col.placeholderKey === key);
    let aData = queryData[index];
    if (aData.condition && aData.condition[conditionIndex]) {
      let conditionData = aData.condition[conditionIndex];
      conditionData.logic = logic;
      conditionData.key = key;
      conditionData.function = func;
      conditionData.value = value;
      conditionData.columnName = column ? column.name : null;
      // Session Id and Database Id don't have type equipped in their structure
      conditionData.type = column?.type ? column.type : 'VARCHAR2';
      conditionData.datetimeValue = datetimeValue;

      this.props.setQueryData(queryData);
    }
  };

  deleteQueryItemCondition = (index, conditionIndex, queryData) => {
    let aData = queryData[index];

    if (aData && aData.condition) {
      aData.condition.splice(conditionIndex, 1);
    }

    this.props.setQueryData(queryData);
  };

  addCondition = (index, queryData) => {
    let aData = queryData[index];
    if (!aData.condition) {
      aData.condition = [];
    }
    let condition = {
      key: null,
      function: null,
      value: null,
      logic: 'AND',
    };
    aData.condition.push(condition);
    this.props.setValidateKey(false);
    this.props.setQueryData(queryData);
  };

  duplicateCondition = (index, conditionIndex, queryData) => {
    const aData = queryData[index];
    if (aData && aData.condition && aData.condition[conditionIndex]) {
      const originalCondition = aData.condition[conditionIndex];
      const duplicatedCondition = {
        ...originalCondition,
        logic: 'AND', // Always set to AND for duplicated conditions
      };

      const newConditions = [
        ...aData.condition.slice(0, conditionIndex + 1),
        duplicatedCondition,
        ...aData.condition.slice(conditionIndex + 1),
      ];

      const newQueryData = this.createUpdatedQueryData(queryData, index, {
        condition: newConditions,
      });

      this.props.setValidateKey(false);
      this.props.setQueryData(newQueryData);
    }
  };

  getOutputIcon = key => {
    switch (key) {
      case QUERY_OUTPUT_TYPES.SELECTED_COLUMN:
        return <ViewColumnIcon fontSize="small" />;
      case QUERY_OUTPUT_TYPES.FILTERED_COLUMN:
        return <FilterListIcon fontSize="small" />;
      case QUERY_OUTPUT_TYPES.SELECTED_RECORD:
        return <TableRowsIcon fontSize="small" />;
      case QUERY_OUTPUT_TYPES.FILTERED_RECORD:
        return <ManageSearchIcon fontSize="small" />;
      default:
        return null;
    }
  };

  render() {
    const {
      classes,
      aData,
      index,
      queryData,
      component,
      outputSelect,
      validateKey,
      availableCols,
    } = this.props;

    const column = this.props.availableCols.find(col => col.placeholderKey === aData.value);
    if (column && column.type === 'LIST' && !this.state.showWarning) this.state.showWarning = true;
    return (
      <Grid item xs={12}>
        <CustomPaper className="query-item-paper defaultText">
          <div className="query-item-container">
            {/* Action Buttons - positioned in top right */}
            <div className="query-item-actions">
              <FileCopyOutlined
                className="query-item-copy-icon"
                onClick={() => {
                  this.props.copyThisItem();
                }}
              />
              <CloseIcon
                className="query-item-close-icon"
                onClick={() => this.deleteQueryItem(index, queryData)}
              />
            </div>

            {/* Main Content - now uses full width */}
            <div className="query-item-main-content">
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs="auto">
                  <p className="query-item-placeholder-text">Placeholder</p>
                </Grid>
                <Grid item>
                  <W86PlaceholderTextField
                    value={aData.key}
                    error={this.state.checkKey ? true : false}
                    helperText={this.state.checkKey ? this.state.errorMessage : null}
                    placeholderText="Enter placeholder for query item"
                    handleChange={event => {
                      this.handleKey(event, queryData, index);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container direction="column" sx={{ mt: 1 }}>
                <Grid item>
                  <p className="query-item-output-text">
                    {component === 'query_database_record'
                      ? 'Output'
                      : 'Perform the following data query'}
                  </p>
                </Grid>
                <Grid item>
                  <FormControl fullWidth size="small">
                    <Select
                      value={aData.outputFunc || ''}
                      onChange={event => this.setOutputFunc(index, event, queryData)}
                      displayEmpty
                      className="query-item-select"
                    >
                      <MenuItem value="" disabled>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ListIcon fontSize="small" sx={{ opacity: 0.5 }} />
                          <span>Select output type</span>
                        </Stack>
                      </MenuItem>
                      {outputSelect.map(item => (
                        <MenuItem key={item.key} value={item.key}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {this.getOutputIcon(item.key)}
                            <span>{item.name}</span>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {this.renderQueryItemDetails(aData.outputFunc, aData, index)}
            </div>
          </div>
        </CustomPaper>
      </Grid>
    );
  }
}

export default withStyles(styles)(RenderQueryItem);
